<h2 mat-dialog-title>
  {{ isEditMode ? "Edit Fixed Asset" : "New Fixed Asset" }}
</h2>

<mat-dialog-content>
  <form [formGroup]="form" class="flex flex-col gap-4 py-2">
    <mat-tab-group>
      <mat-tab label="General">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <mat-form-field class="w-full">
            <mat-label>Asset Name</mat-label>
            <input matInput formControlName="name" placeholder="e.g. MacBook Pro M3" />
            @if (form.get("name")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Asset Number</mat-label>
            <input matInput formControlName="assetNumber" placeholder="e.g. AST-001" />
          </mat-form-field>

          <mat-form-field class="w-full md:col-span-2">
            <mat-label>Description</mat-label>
            <textarea matInput formControlName="description"></textarea>
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Entity</mat-label>
            <mat-select formControlName="entityId">
              @for (entity of entities(); track entity.id) {
                <mat-option [value]="entity.id">
                  {{ entity.name }}
                </mat-option>
              }
            </mat-select>
            @if (form.get("entityId")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>

          <div class="flex items-center">
            <mat-slide-toggle formControlName="isActive" color="primary">Active</mat-slide-toggle>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Financials">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <mat-form-field class="w-full">
            <mat-label>Purchase Date</mat-label>
            <input matInput [matDatepicker]="picker" formControlName="purchaseDate" />
            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            @if (form.get("purchaseDate")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Purchase Price</mat-label>
            <input matInput type="number" formControlName="purchasePrice" min="0" step="0.01" />
            <span matTextPrefix>€&nbsp;</span>
            @if (form.get("purchasePrice")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Salvage Value</mat-label>
            <input matInput type="number" formControlName="salvageValue" min="0" step="0.01" />
            <span matTextPrefix>€&nbsp;</span>
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Useful Life (Months)</mat-label>
            <input matInput type="number" formControlName="usefulLifeMonths" min="1" step="1" />
            @if (form.get("usefulLifeMonths")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Depreciation Method</mat-label>
            <mat-select formControlName="depreciationMethod">
              @for (method of depreciationMethods; track method) {
                <mat-option [value]="method">
                  {{ method | titlecase }}
                </mat-option>
              }
            </mat-select>
            @if (form.get("depreciationMethod")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>
        </div>
      </mat-tab>

      <mat-tab label="Accounting">
        <div class="grid grid-cols-1 gap-4 mt-4">
          <p class="text-sm text-[var(--mat-sys-on-surface-variant)] mb-2">
            Select the Ledger Accounts for automatic booking.
          </p>

          <mat-form-field class="w-full">
            <mat-label>Asset Account (Balance Sheet)</mat-label>
            <input
              type="text"
              placeholder="Search Asset Accounts (e.g. 0400)"
              matInput
              formControlName="assetAccount"
              [matAutocomplete]="autoAsset"
            />
            <mat-autocomplete #autoAsset="matAutocomplete" [displayWith]="displayFn">
              @for (account of filteredAssetAccounts(); track account.id) {
                <mat-option [value]="account">
                  <span class="font-mono text-sm mr-2">{{ account.accountCode }}</span>
                  <span>{{ account.accountName }}</span>
                </mat-option>
              }
            </mat-autocomplete>
            @if (form.get("assetAccount")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="w-full">
            <mat-label>Depreciation Expense Account</mat-label>
            <input
              type="text"
              placeholder="Search Expense Accounts (e.g. 4830)"
              matInput
              formControlName="depreciationAccount"
              [matAutocomplete]="autoDep"
            />
            <mat-autocomplete #autoDep="matAutocomplete" [displayWith]="displayFn">
              @for (account of filteredDepreciationAccounts(); track account.id) {
                <mat-option [value]="account">
                  <span class="font-mono text-sm mr-2">{{ account.accountCode }}</span>
                  <span>{{ account.accountName }}</span>
                </mat-option>
              }
            </mat-autocomplete>
            @if (form.get("depreciationAccount")?.hasError("required")) {
              <mat-error>Required</mat-error>
            }
          </mat-form-field>
        </div>
      </mat-tab>
    </mat-tab-group>
  </form>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button (click)="cancel()">Cancel</button>
  <button
    mat-flat-button
    color="primary"
    [disabled]="form.invalid || form.pristine"
    (click)="save()"
  >
    {{ isEditMode ? "Update" : "Create" }}
  </button>
</mat-dialog-actions>
